import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { FaRegEdit } from 'react-icons/fa';
import api from '../../utils/api';
import Stack from "@mui/material/Stack";
import Pagination from "@mui/material/Pagination";
import { IoSearchSharp } from 'react-icons/io5';
import { useSearchParams, useNavigate } from 'react-router';

function HR_LeaveBalance() {
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBalance, setSelectedBalance] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    earnedLeaves: '',
    sickLeaves: '',
    compOffs: '',
    compPays: '',
    totalEarnedLeaves: '',
    totalSickLeaves: '',
  });
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  

  const itemsPerPage = 8;
const applySearch = () => {
  navigate(`?search=${searchInput}`);
  setCurrentPage(1);
};
  const fetchLeaveBalances = async (page = 0) => {
    try {
      setIsLoading(true);
      const response = await api.get('/api/leave-balance/all', {
        params: {
          page,
          size: itemsPerPage,
          sortBy: 'user.id',
          direction: 'asc',
        },
      });
      setLeaveBalances(response.data.content || []);
      setTotalPages(response.data.totalPages || 0);
      console.log('Eleave balance:', response.data.content);
    } catch (error) {
      console.error('Error fetching leave balances:', error);
      toast.error('Failed to fetch leave balances');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveBalances(currentPage - 1);
  }, [currentPage]);

  const filteredBalances = leaveBalances.filter((balance) => {
    const term = searchTerm.toLowerCase();
    return (
      balance.userName?.toLowerCase().includes(term) ||
      balance.userId?.toString().includes(term)
    );
  });

  const handleEditClick = (balance) => {
    setSelectedBalance(balance);
    setLeaveForm({
      earnedLeaves: balance.earnedLeaves ?? '',
      sickLeaves: balance.sickLeaves ?? '',
      compOffs: balance.compOffs ?? '',
      compPays: balance.compPays ?? '',
      totalEarnedLeaves: balance.totalEarnedLeaves ?? '',
      totalSickLeaves: balance.totalSickLeaves ?? '',
    });
    setShowEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBalance) return;

    setIsSubmitting(true);
    try {
      await api.put(`/api/leave-balance/${selectedBalance.userId}`, {
        earnedLeaves: parseFloat(leaveForm.earnedLeaves) || 0,
        sickLeaves: parseFloat(leaveForm.sickLeaves) || 0,
        compOffs: parseFloat(leaveForm.compOffs) || 0,
        compPays: parseFloat(leaveForm.compPays) || 0,
        totalEarnedLeaves: parseFloat(leaveForm.totalEarnedLeaves) || 0,
        totalSickLeaves: parseFloat(leaveForm.totalSickLeaves) || 0,
      });
      toast.success('Leave balance updated successfully');
      setShowEditModal(false);
      setSelectedBalance(null);
      fetchLeaveBalances(currentPage - 1);
    } catch (error) {
      console.error('Error updating leave balance:', error);
      toast.error('Failed to update leave balance');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLeaveForm((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="sm:p-6 mb-20 w-full mx-auto">
      {/* Header */}
      <motion.div
        className="flex justify-between items-center mb-4 bg-white rounded-2xl p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <h2 className="text-xl xs:text-2xl font-bold">Leave Balance Management</h2>
<div className="relative">
  <input
    type="text"
    placeholder="Search employee..."
    value={searchInput}
    onChange={(e) => setSearchInput(e.target.value)}   // only updates local state
    onKeyDown={(e) => {
      if (e.key === 'Enter') applySearch();             // apply on Enter
    }}
    className="pl-9 pr-4 py-2 text-sm rounded-full border border-slate-200 bg-slate-50 focus:outline-none focus:border-slate-400 w-56"
  />
  <IoSearchSharp
    onClick={applySearch}                               // apply on icon click
    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm cursor-pointer hover:text-slate-600"
  />
</div>

      </motion.div>

      {/* Desktop Table */}
      <motion.div
        className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white border-b border-slate-200">
              <tr>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Employee Id</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Employee Name</th>
                <th className="px-3 py-4 text-sm font-semibold text-blue-600 text-left">Total EL</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Earned Leaves</th>
                <th className="px-3 py-4 text-sm font-semibold text-green-600 text-left">Total SL</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Sick Leaves</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Comp Offs</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Comp Pay</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                    </div>
                  </td>
                </tr>
              ) : filteredBalances.length > 0 ? (
                filteredBalances.map((balance) => (
                  <tr key={balance.id} className="hover:bg-slate-50/50 transition-colors duration-150">
                    <td className="px-3 py-2 text-sm text-slate-800 font-medium">
                      {balance.employeeId || '—'}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-800 font-medium">
                      {balance.userName || '—'}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                        {balance.totalEarnedLeaves ?? '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-600">{balance.earnedLeaves ?? '—'}</td>
                    <td className="px-3 py-2 text-sm">
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700">
                        {balance.totalSickLeaves ?? '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-600">{balance.sickLeaves ?? '—'}</td>
                    <td className="px-3 py-2 text-sm text-slate-600">{balance.compOffs ?? '—'}</td>
                    <td className="px-3 py-2 text-sm text-slate-600">{balance.compPays ?? '—'}</td>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex justify-center">
                        <motion.button
                          onClick={() => handleEditClick(balance)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Edit"
                        >
                          <FaRegEdit className="text-xl" />
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-slate-400">
                    {searchTerm ? 'No matching results found' : 'No leave balance records found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-3 py-2 border-t border-slate-100">
          <Stack spacing={2} className="flex justify-end">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              variant="outlined"
              shape="rounded"
              size="small"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": { color: "#475569", borderColor: "#e2e8f0" },
                "& .Mui-selected": { backgroundColor: "#475569 !important", color: "#fff" },
              }}
            />
          </Stack>
        </div>
      </motion.div>

      {/* Mobile Cards */}
      <div className="sm:hidden space-y-4">
        {isLoading ? (
          <div className="bg-white p-5 rounded-xl shadow-md border border-slate-100">
            <div className="flex justify-center py-8">
              <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </div>
          </div>
        ) : filteredBalances.length > 0 ? (
          filteredBalances.map((balance) => (
            <div key={balance.id} className="bg-white p-5 rounded-xl shadow-md border border-slate-100">
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <h3 className="font-semibold text-slate-800">{balance.userName || '—'}</h3>
                  <motion.button
                    onClick={() => handleEditClick(balance)}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaRegEdit className="text-xl" />
                  </motion.button>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <span className="text-xs text-blue-500 font-medium">Total EL:</span>
                    <p className="font-medium text-slate-700">{balance.totalEarnedLeaves ?? '—'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500 text-xs">Earned:</span>
                    <p className="font-medium text-slate-700">{balance.earnedLeaves ?? '—'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-green-500 font-medium">Total SL:</span>
                    <p className="font-medium text-slate-700">{balance.totalSickLeaves ?? '—'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500 text-xs">Sick:</span>
                    <p className="font-medium text-slate-700">{balance.sickLeaves ?? '—'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500 text-xs">Comp Off:</span>
                    <p className="font-medium text-slate-700">{balance.compOffs ?? '—'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500 text-xs">Comp Pay:</span>
                    <p className="font-medium text-slate-700">{balance.compPays ?? '—'}</p>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white p-8 rounded-xl text-center text-slate-400">
            {searchTerm ? 'No matching results found' : 'No leave balance records found'}
          </div>
        )}

        <div className="mt-6 flex justify-center">
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              variant="outlined"
              shape="rounded"
              size="medium"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": { color: "#475569", borderColor: "#e2e8f0" },
                "& .Mui-selected": { backgroundColor: "#475569 !important", color: "#fff" },
              }}
            />
          </Stack>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedBalance && (
        <div
          className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (!isSubmitting && e.target === e.currentTarget) {
              setShowEditModal(false);
              setSelectedBalance(null);
            }
          }}
        >
          <motion.div
            className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-bold text-slate-800">Edit Leave Balance</h3>
                <p className="text-sm text-slate-500">{selectedBalance.userName || '—'}</p>
              </div>
              <button
                onClick={() => {
                  if (!isSubmitting) {
                    setShowEditModal(false);
                    setSelectedBalance(null);
                  }
                }}
                disabled={isSubmitting}
                className={`text-slate-400 hover:text-slate-600 text-2xl ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                ✕
              </button>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>

              {/* Total EL & Total SL side by side */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-blue-600 mb-1">Total Earned Leaves</label>
                  <input
                    type="number"
                    name="totalEarnedLeaves"
                    min="0"
                    step="0.5"
                    value={leaveForm.totalEarnedLeaves}
                    onChange={handleInputChange}
                    className="w-full border border-slate-200 rounded-lg p-3 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-green-600 mb-1">Total Sick Leaves</label>
                  <input
                    type="number"
                    name="totalSickLeaves"
                    min="0"
                    step="0.5"
                    value={leaveForm.totalSickLeaves}
                    onChange={handleInputChange}
                    className="w-full border border-slate-200 rounded-lg p-3 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Earned Leaves (Remaining)</label>
                <input
                  type="number"
                  name="earnedLeaves"
                  min="0"
                  step="0.5"
                  value={leaveForm.earnedLeaves}
                  onChange={handleInputChange}
                  className="w-full border border-slate-200 rounded-lg p-3 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Sick Leaves (Remaining)</label>
                <input
                  type="number"
                  name="sickLeaves"
                  min="0"
                  step="0.5"
                  value={leaveForm.sickLeaves}
                  onChange={handleInputChange}
                  className="w-full border border-slate-200 rounded-lg p-3 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Comp Offs</label>
                <input
                  type="number"
                  name="compOffs"
                  min="0"
                  step="0.5"
                  value={leaveForm.compOffs}
                  onChange={handleInputChange}
                  className="w-full border border-slate-200 rounded-lg p-3 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Comp Pay</label>
                <input
                  type="number"
                  name="compPays"
                  min="0"
                  step="0.5"
                  value={leaveForm.compPays}
                  onChange={handleInputChange}
                  className="w-full border border-slate-200 rounded-lg p-3 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  placeholder="0"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedBalance(null);
                  }}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium bg-slate-800 text-white rounded-full hover:bg-slate-700
                             disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Saving...</span>
                    </div>
                  ) : (
                    <span>Save Changes</span>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default HR_LeaveBalance;